import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth.store'

const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { requiresAuth: false },
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'Dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
        meta: { title: 'Tổng quan' },
      },
      { path: 'dashboard', redirect: '/' },
      {
        path: 'cash',
        name: 'Cash',
        component: () => import('@/views/accounting/CashView.vue'),
        meta: { title: 'Tiền mặt' },
      },
      {
        path: 'cashier',
        name: 'Cashier',
        component: () => import('@/views/cashier/CashierView.vue'),
        meta: { title: 'Thủ quỹ' },
      },
      {
        path: 'warehouse',
        name: 'Warehouse',
        component: () => import('@/views/warehouse/WarehouseView.vue'),
        meta: { title: 'Thủ kho' },
      },
      {
        path: 'cash/receipt/new',
        name: 'ReceiptNew',
        component: () => import('@/views/accounting/ReceiptFormView.vue'),
        meta: { title: 'Phiếu thu' },
      },
      {
        path: 'cash/receipt/:id',
        name: 'ReceiptEdit',
        component: () => import('@/views/accounting/ReceiptFormView.vue'),
        meta: { title: 'Phiếu thu' },
      },
      {
        path: 'cash/payment/new',
        name: 'PaymentNew',
        component: () => import('@/views/accounting/PaymentFormView.vue'),
        meta: { title: 'Phiếu chi' },
      },
      {
        path: 'cash/payment/:id',
        name: 'PaymentEdit',
        component: () => import('@/views/accounting/PaymentFormView.vue'),
        meta: { title: 'Phiếu chi' },
      },
      {
        path: 'cash/receipt-by-invoice',
        name: 'CashReceiptByInvoice',
        component: () => import('@/views/accounting/CashReceiptByInvoiceView.vue'),
        meta: { title: 'Thu tiền theo hóa đơn' },
      },
      {
        path: 'cash/receipt-voucher/:id',
        name: 'CashReceiptVoucher',
        component: () => import('@/views/accounting/CashReceiptVoucherView.vue'),
        meta: { title: 'Chứng từ thu/chi' },
      },
      {
        path: 'cash/payment-by-invoice',
        name: 'CashPaymentByInvoice',
        component: () => import('@/views/accounting/CashPaymentBySupplierView.vue'),
        meta: { title: 'Chi tiền theo hóa đơn' },
      },
      {
        path: 'cash/inventory/new',
        name: 'InventoryNew',
        component: () => import('@/views/accounting/InventoryFormView.vue'),
        meta: { title: 'Kiểm kê quỹ' },
      },
      {
        path: 'cash/payroll',
        name: 'PayrollPayment',
        component: () => import('@/views/accounting/PayrollPaymentView.vue'),
        meta: { title: 'Trả lương' },
      },
      {
        path: 'cash/tax-payment',
        name: 'TaxPayment',
        component: () => import('@/views/accounting/TaxPaymentView.vue'),
        meta: { title: 'Nộp thuế' },
      },
      {
        path: 'bank',
        name: 'Bank',
        component: () => import('@/views/accounting/BankView.vue'),
        meta: { title: 'Tiền gửi' },
      },
      {
        path: 'purchase',
        name: 'Purchase',
        component: () => import('@/views/accounting/PurchaseView.vue'),
        meta: { title: 'Mua hàng' },
      },
      {
        path: 'sales',
        name: 'Sales',
        component: () => import('@/views/accounting/SalesView.vue'),
        meta: { title: 'Bán hàng' },
      },
      // Chứng từ bán hàng — popup-mode redirects (thay vì standalone page)
      { path: 'sales/voucher/new', redirect: '/sales?tab=sales&popup=SalesVoucher' },
      { path: 'sales/voucher/:id', redirect: (to) => `/sales?tab=sales&popup=SalesVoucher&popupId=${to.params.id}` },
      // Báo giá / Đơn đặt hàng / Hợp đồng bán hàng / Hóa đơn — popup-mode redirects
      { path: 'sales/quotes',    redirect: '/sales?popup=Quote' },
      { path: 'sales/orders',    redirect: '/sales?popup=SalesOrder' },
      { path: 'sales/contracts', redirect: '/sales?popup=SalesContract' },
      { path: 'sales/invoices',  redirect: '/sales?tab=invoices' },
      {
        path: 'categories',
        name: 'Categories',
        component: () => import('@/views/categories/CategoriesView.vue'),
        meta: { title: 'Danh mục' },
      },
      {
        path: 'customers',
        name: 'Customers',
        component: () => import('@/views/customers/CustomersView.vue'),
        meta: { title: 'Khách hàng' },
      },
      {
        path: 'customers/groups',
        name: 'CustomerGroups',
        component: () => import('@/views/customers/CustomerGroupsView.vue'),
        meta: { title: 'Nhóm khách hàng, nhà cung cấp' },
      },
      {
        path: 'suppliers',
        name: 'Suppliers',
        component: () => import('@/views/accounting/SuppliersView.vue'),
        meta: { title: 'Nhà cung cấp' },
      },
      {
        path: 'hr',
        name: 'HR',
        component: () => import('@/views/hr/HRView.vue'),
        meta: { title: 'Nhân viên' },
      },
      {
        path: 'payroll',
        name: 'Payroll',
        component: () => import('@/views/accounting/PayrollView.vue'),
        meta: { title: 'Tiền lương' },
      },
      {
        path: 'tax',
        name: 'Tax',
        component: () => import('@/views/tax/TaxDeclarationListView.vue'),
        meta: { title: 'Khai thuế' },
      },
      {
        path: 'reports',
        name: 'Reports',
        component: () => import('@/views/reports/ReportsView.vue'),
        meta: { title: 'Báo cáo' },
      },
      {
        path: 'settings',
        name: 'Settings',
        component: () => import('@/views/settings/SettingsView.vue'),
        meta: { title: 'Cài đặt' },
      },
      {
        path: 'settings/warning-rules',
        name: 'WarningRules',
        component: () => import('@/views/settings/WarningRulesView.vue'),
        meta: { title: 'Cảnh báo nghiệp vụ' },
      },
      {
        path: 'settings/backup',
        name: 'Backup',
        component: () => import('@/views/settings/BackupView.vue'),
        meta: { title: 'Sao lưu & phục hồi' },
      },
      {
        path: 'settings/users',
        name: 'UserSettings',
        component: () => import('@/views/settings/UserSettingsView.vue'),
        meta: { title: 'Người dùng & phân quyền' },
      },
      {
        path: 'settings/profile',
        name: 'Profile',
        component: () => import('@/views/settings/ProfileView.vue'),
        meta: { title: 'Hồ sơ cá nhân' },
      },
      {
        path: 'settings/workspaces',
        name: 'WorkspaceSettings',
        component: () => import('@/views/settings/WorkspaceSettingsView.vue'),
        meta: { title: 'Workspace' },
      },
      {
        path: 'recycle-bin',
        name: 'RecycleBin',
        component: () => import('@/views/RecycleBinView.vue'),
        meta: { title: 'Thùng rác' },
      },
      {
        path: ':pathMatch(.*)*',
        name: 'NotFound',
        component: () => import('@/views/error/NotFoundView.vue'),
        meta: { title: 'Không tìm thấy trang' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFoundRoot',
    component: () => import('@/views/error/NotFoundView.vue'),
    meta: { requiresAuth: true },
  },
]

const router = createRouter({
  history: createWebHistory(),
  routes,
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // Đảm bảo init() đã chạy xong (fetch /auth/me) trước khi check auth.
  // Tránh race: route guard chạy trước khi /auth/me trả về → bị đẩy ra /login.
  if (!authStore.isInitialized) {
    await authStore.init()
  }

  const requiresAuth = to.meta.requiresAuth !== false

  if (requiresAuth && !authStore.isAuthenticated) {
    next({ name: 'Login' })
    return
  }

  if (to.name === 'Login' && authStore.isAuthenticated) {
    next({ path: '/' })
    return
  }

  next()
})

export default router
